import { useQueryClient } from "@tanstack/react-query";
import { useCreate, useDelete, useGetList, useNotify } from "ra-core";
import { useCallback, useMemo } from "react";

import {
  isSavedEchoesFilterOn,
  SAVED_ECHOES_RESOURCE,
} from "../providers/commons/savedEchoes";
import type { EchoSave } from "../types";

const SAVES_STALE_MS = 60 * 1000;
const ECHO_LIST_RESOURCE = "echoflicks_echoes";

/**
 * The signed-in CRM user's saved echoes ("Like & Save").
 *
 * One list query, shared by every heart on the page through React Query's
 * cache (the table is RLS-scoped, so no user filter is needed). `toggle`
 * inserts or deletes the row and refreshes the saves plus any echo list that
 * is filtered to saved echoes; other echo lists are left alone, since a save
 * cannot change their rows and a refetch there is a full server page.
 */
export function useEchoSaves() {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { data, isPending, error } = useGetList<EchoSave>(
    SAVED_ECHOES_RESOURCE,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "created_at", order: "DESC" },
    },
    { staleTime: SAVES_STALE_MS, retry: 1 },
  );
  const [create, { isPending: creating }] = useCreate<EchoSave>();
  const [remove, { isPending: removing }] = useDelete<EchoSave>();

  const byEchoId = useMemo(
    () => new Map((data ?? []).map((save) => [String(save.echo_id), save])),
    [data],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [SAVED_ECHOES_RESOURCE] });
    void queryClient.invalidateQueries({
      queryKey: [ECHO_LIST_RESOURCE],
      // ra-core list keys are [resource, "getList", { filter, ... }].
      predicate: (query) => {
        const params = query.queryKey[2] as
          | { filter?: Record<string, unknown> }
          | undefined;
        return isSavedEchoesFilterOn(params?.filter);
      },
    });
  }, [queryClient]);

  const fail = useCallback(
    (caught: unknown) => {
      notify(
        caught instanceof Error && caught.message
          ? caught.message
          : "Could not update your saved echoes",
        { type: "error" },
      );
      refresh();
    },
    [notify, refresh],
  );

  const toggle = useCallback(
    (echoId: string) => {
      const existing = byEchoId.get(echoId);
      if (existing) {
        remove(
          SAVED_ECHOES_RESOURCE,
          { id: existing.id, previousData: existing },
          { onSuccess: refresh, onError: fail },
        );
      } else {
        create(
          SAVED_ECHOES_RESOURCE,
          { data: { echo_id: echoId } },
          { onSuccess: refresh, onError: fail },
        );
      }
    },
    [byEchoId, create, fail, refresh, remove],
  );

  return {
    isSaved: (echoId: string) => byEchoId.has(echoId),
    savedCount: byEchoId.size,
    toggle,
    isPending,
    isMutating: creating || removing,
    error,
  };
}
